/*
 * Optimizely Feature Experimentation — client-side conversion events.
 *
 * Lazily creates one browser SDK instance (code-split via the optimizely-sdk
 * webpack cacheGroup) and tracks conversions against the same visitor id and
 * attributes the server buckets with. Default event dispatching is kept:
 * localStorage pending-queue retry + sendBeacon flush on pagehide.
 */
import {pruneScalars} from './optimizely-attributes'

let context = {enabled: false, sdkKey: null, userId: null, attributes: {}}
let clientPromise = null
let clientSdkKey = null

/* Impressions: queued for the A/B arms a render kept, flushed after commit.
 * One per flag per page view — the caller supplies the page-view key. */
const pendingImpressions = new Map()
const sentImpressions = new Set()
let impressionPageKey = null

export const setFxTrackingContext = (next) => {
    context = {...context, ...next}
    // Child effects queue before _app's effect fills the context — flush now.
    flushFxImpressions()
}

export const getFxTrackingContext = () => context

export const getFxClient = (sdkKey) => {
    if (clientPromise && clientSdkKey === sdkKey) return clientPromise
    clientSdkKey = sdkKey
    clientPromise = (async () => {
        const {default: optimizely} = await import('@optimizely/optimizely-sdk')
        const instance = optimizely.createInstance({
            sdkKey,
            datafileOptions: {autoUpdate: false},
            odpOptions: {disabled: true},
            logLevel: optimizely.enums?.LOG_LEVEL?.WARNING ?? 3
        })
        if (!instance) throw new Error('Optimizely FX: could not create a client instance')
        const {success, reason} = await instance.onReady({timeout: 10000})
        if (!success) throw new Error(`Optimizely FX client not ready: ${reason}`)
        return instance
    })()
    // Don't cache a failed client — the next event retries.
    clientPromise.catch(() => {
        if (clientSdkKey === sdkKey) {
            clientPromise = null
            clientSdkKey = null
        }
    })
    return clientPromise
}

/**
 * Record that an A/B variant was kept for render. `expectedVariation` is what
 * /optimizely-decisions returned, so a client/server disagreement can be seen.
 * @param {string} flagKey
 * @param {string} [expectedVariation]
 * @param {string} pageKey identifies the page view (e.g. router location.key)
 */
export const queueFxImpression = (flagKey, expectedVariation, pageKey) => {
    if (typeof window === 'undefined' || !flagKey) return
    if (pageKey !== impressionPageKey) {
        impressionPageKey = pageKey
        pendingImpressions.clear()
        sentImpressions.clear()
    }
    if (!sentImpressions.has(flagKey)) pendingImpressions.set(flagKey, expectedVariation)
}

/**
 * Fire the queued impressions with decision events ON (`decide`). Bucketing is
 * deterministic on userId + attributes, so this lands on the same variation
 * the endpoint returned. Leaves the queue intact while the context is not
 * ready yet. Never throws.
 */
export const flushFxImpressions = () => {
    if (typeof window === 'undefined' || pendingImpressions.size === 0) return
    const {enabled, sdkKey, userId, attributes} = context
    if (!enabled || !sdkKey || !userId) return

    const batch = [...pendingImpressions]
    pendingImpressions.clear()
    batch.forEach(([flagKey]) => sentImpressions.add(flagKey))

    getFxClient(sdkKey)
        .then((client) => {
            const userContext = client.createUserContext(userId, pruneScalars(attributes))
            if (!userContext) return
            batch.forEach(([flagKey, expected]) => {
                const decision = userContext.decide(flagKey)
                const got = decision?.variationKey
                if (expected && got && got !== expected && process.env.NODE_ENV !== 'production') {
                    console.warn(
                        `[Optimizely FX] impression for "${flagKey}" bucketed "${got}" but "${expected}" was rendered`
                    )
                }
            })
        })
        .catch((error) => {
            console.warn('[Optimizely FX] impression failed:', error?.message || error)
        })
}

/**
 * Track one conversion event. Fire-and-forget: no-ops when FX tracking is
 * disabled or the context is incomplete, and never throws.
 * @param {string} eventName event key configured in the Optimizely FX project
 * @param {Object} [eventTags] scalar tags; `revenue` (int cents) and `value` (float) are metrics
 */
export const trackFxEvent = (eventName, eventTags) => {
    if (typeof window === 'undefined') return
    const {enabled, sdkKey, userId, attributes} = context
    if (!enabled || !sdkKey || !userId || !eventName) return

    getFxClient(sdkKey)
        .then((client) => {
            const userContext = client.createUserContext(userId, pruneScalars(attributes))
            userContext?.trackEvent(eventName, pruneScalars(eventTags))
        })
        .catch((error) => {
            console.warn('[Optimizely FX] trackEvent failed:', eventName, error?.message || error)
        })
}
